import React from 'react';
import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  Menu,
  Row,
} from 'antd';
import {
  EllipsisOutlined,
  PrinterOutlined,
  SwapRightOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import {
  CopySpan,
  DataGrid,
  GreyCardWrapper,
  HeaderTitle,
  InfoButton,
  Label,
  Loading,
  ScreenMask,
  SecondaryButton,
  message,
} from 'components/common';
import SelectWarehouseDialog from 'components/Warehouse/SelectWarehouseDialog';
import { isWarehouseEnableOperationCycle } from 'components/Warehouse/WarehouseSettings';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  LOADING_ICON_SIZE1,
  LS_DEFAULT_WAREHOUSE_KEY,
} from 'constants/config';
import {
  endWarehouseShipmentGroup,
  fetchActiveWarehouseShipmentGroup,
  fetchWarehouseShipmentGroupList,
  startWarehouseShipmentGroup,
} from 'services/shipment';
import { convertTimeByUtc, convertToUtcTime, getWarehouseFromCache } from 'utils';
import PrintShipmentGroupDialog from './PrintShipmentGroupDialog';

const ManifestDocument = () => {
  const DEFAULT_GRID_PAGE_SIZE = 20;
  const { useRef, useState } = React;
  const [activeDuration, setActiveDuration] = useState('');
  const [activeShipmentGroup, setActiveShipmentGroup] = useState<StringKAnyVPair>({});
  const [durationVersion, setDurationVersion] = useState(0);
  const [gridCount, setGridCount] = useState(0);
  const [inited, setInited] = useState(false);
  const [lastDurationVersion, setLastDurationVersion] = useState(0);
  const [lastListVersion, setLastListVersion] = useState(0);
  const [listRangeDate, setListRangeDate] = useState<any>([
    moment().add(-7, 'day').startOf('day'),
    moment().endOf('day'),
  ]);
  const [listVersion, setListVersion] = useState(0);
  const [lockScreen, setLockScreen] = useState(false);
  const [operationCycleEnabled, setOperationCycleEnabled] = useState(false);
  const [printShipmentGroupDialogVisible, setPrintShipmentGroupDialogVisible] = useState(false);
  const [recommendWarehouseNum, setRecommendWarehouseNum] = useState<number>();
  const [selectWarehouseDialogVisible, setSelectWarehouseDialogVisible] = useState(false);
  const [selectedShipmentGroup, setSelectedShipmentGroup] = useState<StringKAnyVPair>({});
  const [shipmentGroupList, setShipmentGroupList] = useState<StringKAnyVPair[]>([]);
  const [warehouse, setWarehouse] = useState<StringKAnyVPair>({});
  const activeDurationTimer = useRef<any>(null);
  const gridPageIndex = useRef<number>(0);
  const gridPageSize = useRef<number>(DEFAULT_GRID_PAGE_SIZE);
  const wrapperRef = useRef<any>(null);

  const columns =  [
    {
      defaultFlex: 1,
      header: 'Group Id',
      //maxWidth: 180,
      name: 'code',
      showColumnMenuTool: false,
      //sortable: false,
      render(row: any) {
        const { data } = row;

        return (
          <CopySpan
            inGrid
            value={data.code}
          />
        );
      },
    },
    {
      header: 'Start Time',
      maxWidth: 170,
      name: 'startAt',
      showColumnMenuTool: false,
      //sortable: false,
      //sorter
      render(row: any) {
        return moment(row.data.startAt).year() > 2000 ? getDisplayTimeLabel(row.data.startAt) : '';
      },
    },
    {
      header: 'End Time',
      maxWidth: 170,
      name: 'endAt',
      showColumnMenuTool: false,
      //sortable: false,
      //sorter
      render(row: any) {
        return moment(row.data.endAt).year() > 2000 ? getDisplayTimeLabel(row.data.endAt) : '';
      },
    },
    {
      header: 'Start by',
      maxWidth: 260,
      name: 'startBy',
      showColumnMenuTool: false,
      //sortable: false,
    },
    {
      header: 'End by',
      maxWidth: 260,
      name: 'endBy',
      showColumnMenuTool: false,
      //sortable: false,
    },
    {
      header: 'Action',
      maxWidth: 100,
      //name: 'endBy',
      showColumnMenuTool: false,
      sortable: false,
      textAlign: 'center',
      render(row: any) {
        const { data } = row;

        return (
          //<div className="ant-input-group-wrapper w-none">
          <SecondaryButton
            disabled={data.status !== 2}
            onClick={() => openPrintShipmentGroupDialog(data)}
            size="small"
          >
            <PrinterOutlined />
            Print
          </SecondaryButton>
          //</div>
        );
      },
    },
  ];

  // eslint-disable-next-line
  const bootstrap = async (whNum: number) => {
    const mres = /wn=(\d+)/.exec(window.location.hash);
    const wh = getWarehouseFromCache(whNum);

    if (Array.isArray(mres) && mres.length > 1) {
      const hwNum = Number(mres[1]);

      if (hwNum > 0 && hwNum !== whNum) {
        message.info({
          content: 'Please set the default warehouse to be your target warehouse',
          duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
        });
        setRecommendWarehouseNum(hwNum);
        setSelectWarehouseDialogVisible(true);
        return;
      }
    }

    if (wh) {
      const enable = await isWarehouseEnableOperationCycle(whNum, setLockScreen);

      if (enable) {
        //whFound = true;
        setOperationCycleEnabled(enable);
        setWarehouse(wh);
        loadShipmentGroupList(wh.warehouseNum);
        getWarehouseActiveShipmentGroup(wh.warehouseNum);
      }
    }
  };

  // eslint-disable-next-line
  const calculateManifestDuration = () => {
    const ts = getDurationBetweenTwoTimes(convertTimeByUtc(activeShipmentGroup.startAt), moment());

    //console.log('cal', ts);
    setActiveDuration(ts);
  };

  const clearActiveDurationTimer = () => {
    if (activeDurationTimer.current) {
      clearTimeout(activeDurationTimer.current);
      activeDurationTimer.current = null;
    }
  };

  const clearActiveShipmentGroup = () => {
    setActiveShipmentGroup({});
    setActiveDuration('');
    clearActiveDurationTimer();
  };

  const closeOutShipmentGroupOfDay = async (wn: number, sgn: number) => {
    setLockScreen(true);
    try {
      const res = await endWarehouseShipmentGroup(wn, sgn);

      //console.log('res', res);
      if (res && typeof res === 'object') {
        clearActiveShipmentGroup();
        loadShipmentGroupList(wn);
        getWarehouseActiveShipmentGroup(wn);
        message.success({
          content: 'Closed out successfully',
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
      }
    } catch(e) {
      message.error({
        content: `Closed out error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    }
  };

  const closePrintShipmentGroupDialog = (sg: StringKAnyVPair) => {
    setPrintShipmentGroupDialogVisible(false);
  };

  const closeSelectWarehouseDialog = () => {
    setSelectWarehouseDialogVisible(false);
  };

  const getWarehouseActiveShipmentGroup = async (wn: number) => {
    try {
      const sg = await fetchActiveWarehouseShipmentGroup(wn);

      if (sg) {
        setActiveShipmentGroup(sg);
        setDurationVersion(durationVersion + 1);
      }
    } catch(e) {
      message.error({
        content: `Fetched active record error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
    }
  };

  const getDisplayTimeLabel = (time: any) => {
    //return moment(convertTimeByUtc(time)).format('YYYY/MM/DD h:mm:ss a');
    return time ? moment(convertTimeByUtc(time)).format('MM/DD/YYYY HH:mm:ss') : '';
  };

  const getDurationBetweenTwoTimes = (t1: any, t2: any) => {
    if (!t1 || !t2) return '';

    const startTime = moment(t1);
    const endTime = moment(t2);
    const diff = endTime.diff(startTime);
    const duration = moment.duration(diff);
    const hours = Math.floor(duration.asHours());
    const mins = duration.minutes();
    const seconds = duration.seconds();

    return `${hours}:${mins}:${seconds}`;
  };

  // eslint-disable-next-line
  const loadShipmentGroupList = async (wn: number) => {
    //console.log('wn', wn);
    setLockScreen(true);
    try {
      const params = {
        $skip: gridPageIndex.current * gridPageSize.current,
        $sortBy: 'startAt+desc',
        $top: gridPageSize.current,
        //startDate: '2026-04-06T16:00:00@2026-04-09T15:59:59',
        startDate: `${convertToUtcTime(listRangeDate[0].startOf('day').format())}@${convertToUtcTime(listRangeDate[1].endOf('day').format())}`,
      };
      const { list, total } = await fetchWarehouseShipmentGroupList(wn, params) as any;

      //console.log('dd', list, total);
      //console.log('ld', `${convertToUtcTime(listRangeDate[0].startOf('day').format())}@${convertToUtcTime(listRangeDate[1].endOf('day').format())}`);
      setShipmentGroupList([]);
      setGridCount(0);
      if (Array.isArray(list)) {
        setShipmentGroupList([ ...list ]);
        setGridCount(total > 0 ? total : list.length);
      }
      //const g = list.find((e: any) => e.status === 1); g && setActiveShipmentGroup(g);
    } catch(e) {
      message.error({
        content: `Loaded list error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    }
  };

  const onLimitChange = (limit: number) => {
    //gridRef?.current.deselectAll();
    if (gridPageSize.current !== limit) {
      if (gridPageIndex.current * limit >= gridCount) gridPageIndex.current = 0;

      gridPageSize.current = limit;
      loadShipmentGroupList(warehouse.warehouseNum);
    }
  };

  const onSkipChange = (skip: number) => {
    const idx = skip / gridPageSize.current;
    //gridRef?.current.deselectAll();
    //console.log('s', skip, idx);
    if (gridPageIndex.current !== idx) {
      gridPageIndex.current = idx;
      loadShipmentGroupList(warehouse.warehouseNum);
    }
  };

  const onSelectWarehouse = async (wh: StringKAnyVPair) => {
    if (wh) {
      const w = getWarehouseFromCache(wh.id);

      if (w) {
        try {
          const enable = await isWarehouseEnableOperationCycle(wh.id, setLockScreen);

          if (enable) {
            setOperationCycleEnabled(true);
            setWarehouse(w);
            localStorage.setItem(LS_DEFAULT_WAREHOUSE_KEY, wh.id);
            loadShipmentGroupList(w.warehouseNum);
            getWarehouseActiveShipmentGroup(w.warehouseNum);
          } else {
            message.info({
              content: 'Please check your warehouse setting to enable Operation Cycle',
              duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
            });
          }
        } catch(e) {
          message.error({
            content: `Set warehouse error: ${e}`,
            duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
          });
        } finally {
          //setLockScreen(false);
        }
      }
    }
  };

  const openPrintShipmentGroupDialog = (sg: StringKAnyVPair) => {
    setSelectedShipmentGroup(sg);
    setPrintShipmentGroupDialogVisible(true);
  };

  const renderDatePicker = (defaultValue: any, onChange: any) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <DatePicker.RangePicker
          allowClear={false}
          disabled={!operationCycleEnabled}
          size="small"
          style={{ width: 230 }}
          value={defaultValue}
          format="MM/DD/YYYY"
          // onOpenChange={()=>{
          //     document.addEventListener('click', (e) => {
          //       e.stopPropagation();
          //     }, { once: true });
          // }}
          ranges={{
            Today: [moment().startOf('day'), moment().endOf('day')],
            Yesterday: [
              moment().add(-1, 'day').startOf('day'),
              moment().add(-1, 'day').endOf('day'),
            ],
            '2 days ago': [
              moment().add(-2, 'day').startOf('day'),
              moment().endOf('day'),
            ],
            '3 days ago': [
              moment().add(-3, 'day').startOf('day'),
              moment().endOf('day'),
            ],
            'This Week': [moment().startOf('week'), moment().endOf('week')],
            'Last Week': [
              moment().add(-1, 'week').startOf('week'),
              moment().add(-1, 'week').endOf('week'),
            ],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
          }}
          onChange={(dates) => onChange(dates)}
          //onChange={(dates: any) => { Array.isArray(dates) && setRangeDateChanged(dates); }}
          //onOpenChange={open => { if (!open && rangeDateChanged.length > 1) { onChange(rangeDateChanged); setRangeDateChanged([]); }}}
        />
      </div>
    );
  };

  const startShipmentGroupOfDay = async (wn: number) => {
    setLockScreen(true);
    try {
      const sg = await startWarehouseShipmentGroup(wn);

      //console.log('sg', sg);
      if (sg && typeof sg === 'object') {
        //loadShipmentGroupList(wn);
        setActiveShipmentGroup(sg);
        setDurationVersion(durationVersion + 1);
        loadShipmentGroupList(warehouse.warehouseNum);
        message.success({
          content: 'Started successfully',
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
      }
    } catch(e) {
      message.error({
        content: `Started error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    }
  };

  React.useEffect(() => {
    if (!inited) {
      const whNum = parseInt(
        localStorage.getItem(LS_DEFAULT_WAREHOUSE_KEY) || '-1'
      );

      if (whNum > 0 /*&& whid*/) {
        bootstrap(whNum);
      } else {
        setSelectWarehouseDialogVisible(true);
      }

      setInited(true);
    }

    if (lastListVersion !== listVersion) {
      loadShipmentGroupList(warehouse.warehouseNum);
      setLastListVersion(listVersion);
    }

    if (wrapperRef.current && lastDurationVersion !== durationVersion) {
      if (moment(activeShipmentGroup.startAt).year() > 2000) {
        calculateManifestDuration();
      }

      setLastDurationVersion(durationVersion);
      activeDurationTimer.current = setTimeout(() => {
        setDurationVersion(durationVersion + 1);
      }, 1000);
    }
  }, [
    activeShipmentGroup,
    bootstrap,
    calculateManifestDuration,
    durationVersion,
    inited,
    lastDurationVersion,
    lastListVersion,
    listVersion,
    loadShipmentGroupList,
    warehouse,
  ]);

  React.useEffect(() => {
    return () => {
      clearActiveDurationTimer();
    }
  }, []);

  return (<>
    <HeaderTitle breadcrumb={['Operation Cycle', 'Manifest Document']} />
    <GreyCardWrapper ref={wrapperRef}>
      <Row justify="center">
        <span className="fs-20">
          <Label className="label-bold tail-colon">Warehouse</Label>
          <span className="text-color-f-1">{warehouse.code || ''}</span>
        </span>
      </Row>
      <Row>
        <Col span={6} />
        <Col span={12}>
          <span className="fs-14">
            <Label className="label-bold tail-colon">Start at</Label>
            <span className="text-color-f-1">{getDisplayTimeLabel(activeShipmentGroup.startAt)}</span>
          </span>
        </Col>
      </Row>
      <Row>
        <Col span={6} />
        <Col span={12}>
          <Row align="middle" justify="space-between">
            <span className="fs-14">
              <Label className="label-bold tail-colon">Hours</Label>
              <span className="text-color-f-1">{activeDuration}</span>
            </span>
            {!activeShipmentGroup.shipmentGroupNum && (
              <Button
                disabled={!operationCycleEnabled}
                onClick={() => startShipmentGroupOfDay(warehouse.warehouseNum)}
                type="primary"
              >
                Start of the Day
              </Button>
            )}
            {activeShipmentGroup.shipmentGroupNum && (
              <InfoButton
                disabled={!operationCycleEnabled}
                onClick={() => closeOutShipmentGroupOfDay(warehouse.warehouseNum, activeShipmentGroup.shipmentGroupNum)}
                type="primary"
              >
                Close-out
              </InfoButton>
            )}
          </Row>
        </Col>
      </Row>
      <Row>
        <Col span={6} />
          <span className="fs-14">
            <Label className="label-bold tail-colon">Start by</Label>
            <span className="text-color-f-1">{activeShipmentGroup.startBy}</span>
          </span>
        <Col span={12}>
        </Col>
      </Row>
    </GreyCardWrapper>
    <GreyCardWrapper className="mt-12">
      <Row className="pr-4" align="middle" justify="end">
        <span className="ant-picker ant-picker-range ant-picker-small fw-400 b-transparent bg-no">{listRangeDate[0].format('MM/DD/YYYY')}<SwapRightOutlined />{listRangeDate[1].format('MM/DD/YYYY')}</span>
        <Dropdown
          overlay={(
            <Menu>
              <Menu.Item key="date-picker">
                {renderDatePicker(listRangeDate, (dr: any) => {setListRangeDate(dr);setListVersion(listVersion + 1);})}
              </Menu.Item>
            </Menu>
          )}
          //trigger={['click']}
        >
          <EllipsisOutlined className="cursor-pointer fs-20" />
        </Dropdown>
      </Row>
      <div className="mt-8">
        <DataGrid
          columns={columns}
          count={gridCount || shipmentGroupList.length}
          dataSource={shipmentGroupList}
          //loading={}
          limit={gridPageSize.current}
          onLimitChange={onLimitChange}
          onSkipChange={onSkipChange}
          pageSizes={[10, 20, 50, 100, 200]}
          pagination
          rowKey="shipmentGroupNum"
          style={{ height: 'calc(100vh - 320px)' }}
        />
      </div>
    </GreyCardWrapper>
    {printShipmentGroupDialogVisible && (
      <PrintShipmentGroupDialog
        maskClosable={false}
        onCancel={closePrintShipmentGroupDialog}
        shipmentGroup={selectedShipmentGroup}
        title={`Print ${selectedShipmentGroup.code}`}
        visible={printShipmentGroupDialogVisible}
      />
    )}
    {selectWarehouseDialogVisible && (
      <SelectWarehouseDialog
        defaultEnable
        defaultValue={recommendWarehouseNum}
        onClose={closeSelectWarehouseDialog}
        onSelect={onSelectWarehouse}
        title="Select default warehouse"
        visible={selectWarehouseDialogVisible}
      />
    )}
    {lockScreen && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default ManifestDocument;

export const isManifestCarrier = (cn: number) => {
  return [7].indexOf(cn) > -1;
};
